import { getSchema } from '@tiptap/core';
import type { Extensions } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import type { Schema } from '@tiptap/pm/model';

import { DateNode } from './dateNode';
import type { DateNodeOptions } from './dateNode';
import { FieldNode } from './fieldNode';
import type { FieldNodeOptions } from './fieldNode';
import { FlowAttributes } from './flowAttributes';
import { HighlightMark } from './highlightMark';
import { SignatureNode } from './signatureNode';
import type { SignatureNodeOptions } from './signatureNode';
import { SignaturePairHighlight } from './signaturePairHighlight';
import { FlowTableEditing } from './tableEditing';
import { RuleDecorations } from './ruleDecorations';
import type { RuleDecorationOptions } from './ruleDecorations';

/**
 * The editor schema for a flow document.
 *
 * StarterKit already provides paragraph, text, hardBreak, both list types and the bold/italic/
 * underline marks under exactly the node names the flow model uses, which is why the model was
 * shaped this way: loading a converted .docx is `Node.fromJSON`, not a translation pass.
 */
export interface FlowExtensionOptions extends FieldNodeOptions {
	/** Slot label/role lookup and stamp geometry for signature anchors. */
	signature?: SignatureNodeOptions;
	/** Label lookup for the conditional-content gutter. */
	rule?: RuleDecorationOptions;
	/** Stamp geometry for date anchors. */
	date?: DateNodeOptions;
}

export const flowExtensions = ({
	signature = {},
	rule = {},
	date = {},
	...options
}: FlowExtensionOptions = {}): Extensions => [
	StarterKit.configure({
		// Nothing in the model uses these, and an editor that can produce them would emit
		// nodes the renderer has no op for.
		blockquote: false,
		// `# ` at the start of a line, or a pasted h1–h6, would otherwise make a node the flow
		// schema has no block for — and `fromEditorContent` throws out of `onUpdate` on every
		// keystroke after it. A heading in a deed is a paragraph with its own size anyway.
		heading: false,
		codeBlock: false,
		code: false,
		horizontalRule: false,
		strike: false,
		link: false,
	}),
	TextStyle,
	Color,
	Image,
	// Column widths live on the table in millimetres, so ProseMirror's own pixel-based
	// resizing would be a second, conflicting source of truth.
	Table.configure({ resizable: false }),
	TableRow,
	TableHeader,
	TableCell,
	// Word's table deletion: whole rows, never a table torn at a character.
	FlowTableEditing,
	FlowAttributes,
	// The chip shows a data point's label, so the node needs the lookup the palette holds.
	FieldNode.configure(options),
	// Same arrangement for a signature: the document holds the slot tuple, the host holds the
	// label the signing roles currently give it.
	SignatureNode.configure(signature),
	DateNode.configure(date),
	// Shows which anchor a drag is about to land beside. Watches only — the drop belongs to
	// `handleDrop` — but it asks the same question, so it cannot promise a pair that is refused.
	SignaturePairHighlight,
	// Draws which rule a block belongs to. Decoration only — it never reaches the model, so it
	// cannot reach a deed, which is the same arrangement the highlight mark has.
	RuleDecorations.configure(rule),
	HighlightMark,
];

let cached: Schema | undefined;

/**
 * The schema, built once.
 *
 * Pure — no DOM — so a ProseMirror state can be built without an editor. TipTap derives the same
 * schema internally from the same extension list, so nothing in the app needs this; what it is
 * for is asserting, in a unit test, that the flow model and the editor schema agree. A
 * disagreement shows up as a node going missing at render, not as an error.
 */
export const flowSchema = (): Schema => {
	cached ??= getSchema(flowExtensions());
	return cached;
};
